/**
 * UserInfoNoAccess Component
 *
 * Description:
 * Displays user information for users who don't have access to academy content. Shows personalized user details including name, email, current system date/time, and a welcome message. Provides initial context information for users in the no-access state.
 *
 * Features:
 * - Displays user's full name, email, and system timestamp.
 * - Personalized welcome message with user's first name.
 * - Date and time formatting for system timestamp display.
 * - Translation support for multi-language user interface.
 * - Clean, accessible layout for user information presentation.
 * - Responsive design with CSS modules for consistent styling.
 *
 * Props:
 * @param {Object} dataUser - User data object containing user information.
 * @param {string} dataUser.apellido - User's last name for display.
 * @param {string} dataUser.email - User's email address for display.
 * @param {string} dataUser.nombre - User's first name for personalized welcome.
 * @param {string} dataUser.systemDate - System timestamp for display formatting.
 *
 * Hooks & State:
 * - useTranslation: Provides translation for UI text elements and labels.
 * - Date object: Formats system timestamp for user-friendly display.
 *
 * Render Flow:
 * 1. Creates Date object from system timestamp for formatting.
 * 2. Renders translated title for the information section.
 * 3. Displays user information container with:
 *    - User's last name with translated label
 *    - User's email address with translated label
 *    - Formatted system date and time
 *    - Personalized welcome message with first name
 * 4. Shows initial descriptive text for no-access context.
 *
 * Good Practices / Warnings:
 * - Ensure dataUser object contains all required properties before rendering.
 * - Date formatting uses browser's locale for consistent display.
 * - Component expects valid date string in dataUser.systemDate.
 * - Translation keys should be maintained in access namespace.
 * - Consider error handling for missing or invalid user data.
 */

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import classes from './UserInfoNoAccess.module.css';

const UserInfoNoAccess = props => {
  const { t } = useTranslation(['access']);

  // Format system date for user-friendly display
  const myDate = new Date(props.dataUser.systemDate);

  return (
    <>
      <p className={classes.infoTitle}>{t('title')}</p>
      <aside className={classes.userInfoNoAccessContainer}>
        <p className={classes.item}>
          {t('name')}:{' '}
          <span className={classes.infoData}>
            {props.dataUser.apellido}
          </span>
        </p>
        <p className={classes.item}>
          {t('email')}:{' '}
          <span className={classes.infoData}>
            {props.dataUser.email}
          </span>
        </p>
        <p className={classes.item}>
          {myDate.toDateString()} {myDate.toLocaleTimeString()}
        </p>
        <p className={classes.itemUserName}>
          {t('welcome')} {props.dataUser.nombre}!
        </p>
      </aside>
      <p className={classes.descriptionText}>{t('initial_text')}</p>
    </>
  );
};

UserInfoNoAccess.propTypes = {
  /** User data object containing user information */
  dataUser: PropTypes.shape({
    /** User's last name for display */
    apellido: PropTypes.string.isRequired,
    /** User's email address for display */
    email: PropTypes.string.isRequired,
    /** User's first name for personalized welcome */
    nombre: PropTypes.string.isRequired,
    /** System timestamp for display formatting */
    systemDate: PropTypes.string.isRequired
  }).isRequired
};

UserInfoNoAccess.defaultProps = {};

export default UserInfoNoAccess;
